import { profileNameFromPath } from '@garagegymbros/domain';
import { readSidebarCollapsed } from '../state/sidebarPreference';
import { readCompanyView } from '../state/companyView';
import './skeleton.css';
import { SkeletonBlock } from './SkeletonPrimitives';
import { CommunitySkeleton } from './CommunitySkeletons';
import { CatalogSkeleton } from './CatalogSkeletons';
import { ProfileSkeleton } from './ProfileSkeletons';

export { SkeletonBlock } from './SkeletonPrimitives';

export const SKELETON_VARIANTS = [
  'feed',
  'catalog',
  'catalog-list',
  'catalog-filters',
  'catalog-editor',
  'companies',
  'companies-list',
  'company',
  'company-equipment',
  'business-reviews',
  'manufacturer-equipment',
  'manufacturer-list',
  'manufacturer-form',
  'manufacturer-detail',
  'product',
  'reviews',
  'profile',
  'gym',
  'my-profile',
  'followers',
  'profile-posts',
  'profile-equipment',
  'gym-comments',
  'gym-gallery',
  'members',
  'forum',
  'forum-preview',
  'discussion',
  'trade',
  'trade-detail',
  'messages',
  'inbox',
  'conversation',
  'message-history',
  'message-people',
  'notifications',
  'settings',
  'admin',
  'admin-members',
  'admin-status',
  'admin-records',
  'account-requests',
  'auth-status',
  'security-status',
  'form',
  'search',
  'comments',
  'rows',
  'stories',
] as const;
export type SkeletonVariant = (typeof SKELETON_VARIANTS)[number];

const communityVariants = new Set<string>([
  'feed',
  'members',
  'stories',
  'forum',
  'forum-preview',
  'discussion',
  'trade',
  'trade-detail',
  'messages',
  'inbox',
  'conversation',
  'message-history',
  'message-people',
  'notifications',
  'comments',
]);
const catalogVariants = new Set<string>([
  'catalog',
  'catalog-list',
  'catalog-filters',
  'catalog-editor',
  'companies',
  'companies-list',
  'company',
  'company-equipment',
  'business-reviews',
  'manufacturer-equipment',
  'manufacturer-list',
  'manufacturer-form',
  'manufacturer-detail',
  'product',
  'reviews',
]);

/** One announcement per region; layout modules contain decorative shapes only. */
export function Skeleton({
  variant = 'rows',
  count = 3,
  label = 'Loading content…',
  page = false,
  className = '',
  sampleGallery,
  search = '',
}: {
  variant?: SkeletonVariant;
  count?: number;
  label?: string;
  page?: boolean;
  className?: string;
  sampleGallery?: boolean;
  search?: string;
}) {
  const boundedCount = Number.isFinite(count) ? Math.max(1, Math.min(8, Math.floor(count))) : 3;
  const content = communityVariants.has(variant) ? (
    <CommunitySkeleton variant={variant} count={boundedCount} page={page} />
  ) : catalogVariants.has(variant) ? (
    <CatalogSkeleton variant={variant} count={boundedCount} page={page} />
  ) : (
    <ProfileSkeleton
      variant={variant}
      count={boundedCount}
      page={page}
      sampleGallery={sampleGallery}
      search={search}
    />
  );
  return (
    <div
      className={`skeleton skeleton-${variant}${page ? ' skeleton-page' : ''}${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">{label}</span>
      <div aria-hidden="true">{content}</div>
    </div>
  );
}

export function routeSkeletonVariant(pathname: string): SkeletonVariant {
  const path = pathname.toLowerCase().replace(/\/$/, '');
  if (path === '/catalog') return 'catalog';
  if (path.startsWith('/catalog/')) return 'product';
  if (path === '/companies') return 'companies';
  if (/^\/companies\/[^/]+\/equipment$/.test(path)) return 'manufacturer-equipment';
  if (path.startsWith('/companies/')) return 'company';
  if (path === '/my-gym') return 'gym';
  if (path === '/my-profile') return 'my-profile';
  if (path === '/account/followers') return 'followers';
  if (path === '/discover') return 'members';
  if (/^\/(?:flexes|stories)(?:\/|$)/.test(path)) return 'stories';
  if (path === '/forum' || path === '/forum/new') return 'forum';
  if (path.startsWith('/forum/')) return 'discussion';
  if (path === '/trade' || path === '/trade/new') return 'trade';
  if (path.startsWith('/trade/')) return 'trade-detail';
  if (path.startsWith('/messages/')) return 'conversation';
  if (path === '/messages') return 'messages';
  if (path === '/notifications') return 'notifications';
  if (path === '/settings') return 'settings';
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/profile') || profileNameFromPath(pathname)) return 'profile';
  if (path === '/auth/callback') return 'auth-status';
  if (path.startsWith('/auth/')) return 'form';
  return 'feed';
}

export function PageSkeleton({
  pathname,
  catalogView = 'grid',
  sampleGallery,
  search = '',
}: {
  pathname: string;
  catalogView?: 'grid' | 'list';
  sampleGallery?: boolean;
  search?: string;
}) {
  const routeVariant = routeSkeletonVariant(pathname);
  const variant =
    routeVariant === 'companies' && readCompanyView() === 'list'
      ? 'companies-list'
      : routeVariant === 'catalog' && catalogView === 'list'
        ? 'catalog-list'
        : routeVariant;
  return (
    <div className={`sk-page sk-page-${variant}`}>
      <Skeleton
        variant={variant}
        count={variant === 'feed' ? 2 : 6}
        label="Loading page…"
        page
        sampleGallery={sampleGallery}
        search={search}
      />
    </div>
  );
}

export function AppSkeleton({
  pathname,
  publicProfile = false,
  catalogView,
  sampleGallery,
  search = '',
}: {
  pathname: string;
  publicProfile?: boolean;
  catalogView?: 'grid' | 'list';
  sampleGallery?: boolean;
  search?: string;
}) {
  if (publicProfile)
    return (
      <div className="sk-guest public-profile-shell">
        <div className="topbar public-profile-header sk-guest-header" aria-hidden="true">
          <div className="public-profile-brand">
            <SkeletonBlock className="sk-brand" />
          </div>
          <div className="header-search sk-header-search">
            <SkeletonBlock className="sk-line" />
          </div>
          <div className="public-profile-auth">
            <SkeletonBlock className="sk-guest-login" />
            <SkeletonBlock className="sk-guest-signup" />
          </div>
        </div>
        <main id="main-content" className="sk-guest-content">
          <PageSkeleton
            pathname={pathname}
            catalogView={catalogView}
            sampleGallery={sampleGallery}
            search={search}
          />
        </main>
      </div>
    );
  const collapsed = readSidebarCollapsed();
  return (
    <div className={`app-shell sk-app${collapsed ? ' sidebar-collapsed' : ''}`}>
      <aside
        className={`sidebar sk-sidebar elevation-1${collapsed ? ' sidebar-collapsed' : ''}`}
        aria-hidden="true"
      >
        <div className="sidebar-header">
          <SkeletonBlock className="sk-brand" />
        </div>
        <nav>
          {Array.from({ length: 11 }, (_, index) => (
            <div className="nav-item sk-nav-item" key={index}>
              <SkeletonBlock className="sk-nav-icon" />
              <SkeletonBlock className="sk-line sk-sidebar-copy" />
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom sk-sidebar-bottom">
          <div className="sidebar-profile">
            <SkeletonBlock className="sk-avatar" />
            <SkeletonBlock className="sk-line sk-sidebar-copy" />
          </div>
        </div>
      </aside>
      <div className="app-main sk-app-main">
        <div className="topbar sk-topbar" aria-hidden="true">
          <div className="topbar-location">
            <SkeletonBlock className="sk-line" />
          </div>
          <div className="header-search sk-header-search">
            <SkeletonBlock className="sk-line" />
          </div>
          <div className="topbar-actions">
            <SkeletonBlock className="sk-nav-icon" />
            <SkeletonBlock className="sk-avatar" />
          </div>
        </div>
        <main className="main-content sk-app-content">
          <PageSkeleton
            pathname={pathname}
            catalogView={catalogView}
            sampleGallery={sampleGallery}
            search={search}
          />
        </main>
        <footer className="app-footer sk-footer" aria-hidden="true">
          <SkeletonBlock className="sk-footer-brand" />
          <div>
            {Array.from({ length: 4 }, (_, index) => (
              <SkeletonBlock className="sk-footer-link" key={index} />
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
}
